import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { User, Trash2, LogOut, Shield } from "lucide-react";

export default function Profile() {
  const navigate = useNavigate();
  const { user, loading, refresh, logout, isAdmin } = useAuth();
  const [bp, setBp] = useState(null);

  if (loading) return <div className="mx-auto max-w-2xl px-5 py-20"><div className="h-40 rounded-2xl bg-muted shimmer" /></div>;

  if (!user) {
    return (
      <div className="mx-auto max-w-md px-5 py-20 text-center" data-testid="profile-signin">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold-soft"><User className="h-7 w-7 gold-text" /></div>
        <h1 className="mt-5 font-display text-2xl font-semibold">Your profile</h1>
        <p className="mt-1 text-sm text-muted-foreground">Sign in to manage your birth profile and saved readings.</p>
        <Button data-testid="profile-signin-btn" onClick={() => navigate("/login")} className="mt-6 rounded-full bg-foreground px-8 text-background">Sign in</Button>
      </div>
    );
  }

  const profile = bp || user.birth_profile || { dob: "", birth_time: "", birthplace: null };
  const setP = (patch) => setBp({ ...profile, ...patch });

  const saveProfile = async () => {
    try { await api.put("/auth/profile", { birth_profile: profile, consent: true }); await refresh(); toast.success("Profile updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteData = async () => {
    try { await api.put("/auth/profile", { birth_profile: null }); await refresh(); setBp({ dob: "", birth_time: "", birthplace: null }); toast.success("Your birth data was deleted"); }
    catch { toast.error("Could not delete data"); }
  };

  const deleteAccount = async () => {
    try { await api.delete("/auth/delete"); toast.success("Account deleted"); logout(); navigate("/"); }
    catch { toast.error("Could not delete account"); }
  };

  return (
    <div className="mx-auto max-w-2xl px-5 pt-8 md:pt-12">
      <div className="flex items-center gap-4">
        {user.picture ? <img src={user.picture} alt="" className="h-14 w-14 rounded-full object-cover" /> :
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gold-soft font-display text-xl gold-text">{(user.name || "J")[0]}</div>}
        <div>
          <h1 className="font-display text-2xl font-semibold">{user.name}</h1>
          <p className="text-sm text-muted-foreground">{user.email || user.phone}</p>
        </div>
        {isAdmin && <button onClick={() => navigate("/admin")} className="ml-auto flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs"><Shield className="h-3.5 w-3.5" /> Admin</button>}
      </div>

      <div className="card-soft mt-8 rounded-2xl p-6">
        <h2 className="font-display text-lg font-semibold">Birth profile</h2>
        <p className="mt-1 text-xs text-muted-foreground">Saved with your consent so we don't ask every time.</p>
        <div className="mt-5 space-y-4">
          <div><label className="mb-1.5 block text-sm font-medium">Date of birth</label>
            <Input data-testid="profile-dob" type="date" value={profile.dob || ""} onChange={(e) => setP({ dob: e.target.value })} className="rounded-xl bg-background" /></div>
          <div><label className="mb-1.5 block text-sm font-medium">Birth time</label>
            <Input data-testid="profile-time" type="time" value={profile.birth_time || ""} onChange={(e) => setP({ birth_time: e.target.value })} className="rounded-xl bg-background" /></div>
          <div><label className="mb-1.5 block text-sm font-medium">Birth city</label>
            <Input data-testid="profile-city" value={profile.birthplace?.city || ""} onChange={(e) => setP({ birthplace: { ...(profile.birthplace || {}), city: e.target.value } })} placeholder="City" className="rounded-xl bg-background" /></div>
        </div>
        <Button data-testid="profile-save" onClick={saveProfile} className="mt-5 rounded-full bg-foreground text-background hover:bg-foreground">Save birth profile</Button>
      </div>

      <div className="card-soft mt-5 rounded-2xl p-6">
        <h2 className="font-display text-lg font-semibold">Privacy & data</h2>
        <p className="mt-1 text-sm text-muted-foreground">You control your personal data at all times.</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <AlertDialog>
            <AlertDialogTrigger asChild><Button data-testid="delete-data" variant="outline" className="rounded-full"><Trash2 className="mr-2 h-4 w-4" /> Delete my birth data</Button></AlertDialogTrigger>
            <AlertDialogContent className="rounded-2xl">
              <AlertDialogHeader><AlertDialogTitle>Delete birth data?</AlertDialogTitle><AlertDialogDescription>This removes your saved DOB, time and place. Your account stays.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-data" onClick={deleteData} className="rounded-full">Delete data</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button data-testid="delete-account" variant="outline" className="rounded-full border-destructive/40 text-destructive"><Trash2 className="mr-2 h-4 w-4" /> Delete account</Button></AlertDialogTrigger>
            <AlertDialogContent className="rounded-2xl">
              <AlertDialogHeader><AlertDialogTitle>Delete your account?</AlertDialogTitle><AlertDialogDescription>This permanently deletes your account, readings and data. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-account" onClick={deleteAccount} className="rounded-full bg-destructive">Delete account</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} variant="ghost" className="mt-6 rounded-full text-muted-foreground"><LogOut className="mr-2 h-4 w-4" /> Sign out</Button>
    </div>
  );
}
